// UsersMuiEditor.jsx
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import clientConfig from '../../../client-config';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchUsers = async () => {
    const res = await axios.get(`${clientConfig.backendHOST}/user/Users`, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'x-auth-token': localStorage.token,
      },
    });
    setUsers(res.data);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCheckboxChange = async (userId, field, value) => {
    const updatedUser = users.find((u) => u.userId === userId);
    const updated = { ...updatedUser, [field]: value };
    await axios.put(`${clientConfig.backendHOST}/user/Users`, updated, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'x-auth-token': localStorage.token,
      },
    });
    fetchUsers();
  };

  const toggleExpand = (userId) => {
    setExpandedUserId(expandedUserId === userId ? null : userId);
  };

  const filteredUsers = users.filter((user) => {
    const fullName = `${user.lastName} ${user.firstName}`.toLowerCase();
    return (
      fullName.includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  return (
    <Box p={3} display='flex' flexDirection='column' alignItems='center'>
      <Typography variant='h4' gutterBottom>
        Felhasználók
      </Typography>
      <TextField
        variant='outlined'
        placeholder='Keresés név vagy email alapján...'
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        fullWidth
        sx={{ mb: 2, maxWidth: 600 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Vezetéknév</TableCell>
                <TableCell>Keresztnév</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Admin</TableCell>
                <TableCell>Tesztelő</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((user) => (
                  <React.Fragment key={user.userId}>
                    <TableRow>
                      <TableCell>
                        <IconButton
                          size='small'
                          onClick={() => toggleExpand(user.userId)}
                        >
                          {expandedUserId === user.userId ? (
                            <KeyboardArrowUp />
                          ) : (
                            <KeyboardArrowDown />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>{user.lastName}</TableCell>
                      <TableCell>{user.firstName}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Checkbox
                          checked={user.isAdmin === 1}
                          onChange={(e) =>
                            handleCheckboxChange(
                              user.userId,
                              'isAdmin',
                              e.target.checked ? 1 : 0
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          checked={user.isTester === 1}
                          onChange={(e) =>
                            handleCheckboxChange(
                              user.userId,
                              'isTester',
                              e.target.checked ? 1 : 0
                            )
                          }
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ paddingBottom: 0, paddingTop: 0 }}
                      >
                        <Collapse
                          in={expandedUserId === user.userId}
                          timeout='auto'
                          unmountOnExit
                        >
                          <Box margin={2}>
                            <Typography variant='subtitle1'>
                              Előfizetések:
                            </Typography>
                            <UserSubscriptions userId={user.userId} />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component='div'
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>
    </Box>
  );
};

const UserSubscriptions = ({ userId }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [editedSubs, setEditedSubs] = useState({});

  useEffect(() => {
    axios
      .get(`${clientConfig.backendHOST}/subscriptions/userSubscription`, {
        params: { onlyUser: userId },
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'x-auth-token': localStorage.token,
        },
      })
      .then((res) => setSubscriptions(res.data))
      .catch((err) => console.error(err));
  }, [userId]);

  const handleChange = (id, field, value) => {
    setEditedSubs((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  const handleSave = (id) => {
    const updated = {
      ...subscriptions.find((s) => s.id === id),
      ...editedSubs[id],
    };
    const allowedFields = ['id', 'subscription_days', 'valid_from', 'valid_to'];
    const cleanedData = Object.fromEntries(
      Object.entries(updated).filter(([key]) => allowedFields.includes(key))
    );
    axios
      .put(
        `${clientConfig.backendHOST}/subscriptions/userSubscription`,
        cleanedData,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': localStorage.token,
          },
        }
      )
      .then(() => {
        setEditedSubs((prev) => {
          const { [id]: removed, ...rest } = prev;
          return rest;
        });
      })
      .catch((err) => console.error(err));
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>Előfizetés</TableCell>
            <TableCell>Napok</TableCell>
            <TableCell>Érvényes tól</TableCell>
            <TableCell>Érvényes ig</TableCell>
            <TableCell>Státusz</TableCell>
            <TableCell>Művelet</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {subscriptions.map((sub) => {
            const edited = editedSubs[sub.id] || {};
            return (
              <TableRow key={sub.id}>
                <TableCell>{sub.subscription_code}</TableCell>
                <TableCell>
                  <TextField
                    size='small'
                    value={edited.subscription_days ?? sub.subscription_days}
                    onChange={(e) =>
                      handleChange(sub.id, 'subscription_days', e.target.value)
                    }
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size='small'
                    type='date'
                    value={
                      edited.valid_from ?? sub.valid_from?.split('T')[0] ?? ''
                    }
                    onChange={(e) =>
                      handleChange(sub.id, 'valid_from', e.target.value)
                    }
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size='small'
                    type='date'
                    value={edited.valid_to ?? sub.valid_to?.split('T')[0] ?? ''}
                    onChange={(e) =>
                      handleChange(sub.id, 'valid_to', e.target.value)
                    }
                  />
                </TableCell>
                <TableCell>{sub.Status}</TableCell>
                <TableCell>
                  {editedSubs[sub.id] && (
                    <IconButton
                      color='primary'
                      onClick={() => handleSave(sub.id)}
                    >
                      <SaveIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default Users;
